import React, { useState } from 'react';
import { Modal, Form, Input, Switch, message, ColorPicker, Select } from 'antd';
import { createVocabularyList } from '@/libs/api';
import type { components } from '@/api/types';

type VocabularyListDto = components['schemas']['VocabularyListDto'];

interface CreateVocabularyModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: (list: VocabularyListDto) => void;
}

const DEFAULT_THEME_COLOR = '#6a0dad';
const MAX_TAGS = 5;

// 预设的主题颜色
const PRESET_COLORS = {
  recommended: [
    '#6a0dad', // 主色紫色
    '#b555fa', // 次要紫色
    '#eee1f8', // 浅紫色
    '#52C41A', // 绿色
    '#FAAD14', // 黄色
    '#F5222D', // 红色
    '#13C2C2', // 青色
    '#FA8C16', // 橙色
    '#666666', // 灰色
    '#000000', // 黑色
  ],
};

const CreateVocabularyModal: React.FC<CreateVocabularyModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const response = await createVocabularyList({
        name: values.name,
        isPublic: values.isPublic || false,
        themeColor: values.themeColor?.toHexString() || DEFAULT_THEME_COLOR,
        tags: values.tags || [],
      });
      message.success('创建成功');
      form.resetFields();
      onSuccess(response);
      onCancel();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('创建失败');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="新建生词本"
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="生词本名称"
          rules={[{ required: true, message: '请输入生词本名称' }]}
        >
          <Input placeholder="请输入生词本名称" />
        </Form.Item>

        <Form.Item name="themeColor" label="主题颜色">
          <ColorPicker
            presets={[
              {
                label: '推荐颜色',
                colors: PRESET_COLORS.recommended,
              },
            ]}
            defaultValue={DEFAULT_THEME_COLOR}
            size="large"
            showText
            disabledAlpha
            format="hex"
          />
        </Form.Item>

        <Form.Item name="tags" label="标签">
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="输入标签后按回车"
            maxTagCount={MAX_TAGS}
            maxTagTextLength={10}
          />
        </Form.Item>

        <Form.Item name="isPublic" label="是否公开" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateVocabularyModal;
